import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Field,
  Flex,
  Grid,
  GridItem,
  Input,
  NativeSelect,
  Tag,
  Text,
} from '@chakra-ui/react';
import CpfInput from '@components/CpfInput';
import PortfolioSelect from '@components/PortfolioSelect';
import PhoneInput from '@components/PhoneInput';
import type { PortfolioType } from '@customTypes/portfolio';
import { formatPhoneNumber, onlyDigits } from '@utils/formats';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router';
import { PHONE_NUMBER } from '@utils/regex';
import { useCustomersContext } from '../context';

const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === 'Enter') {
    e.preventDefault();
  }
};

const Filters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultName = searchParams.get('name') || '';
  const defaultEmail = searchParams.get('email') || '';
  const defaultTaxId = searchParams.get('taxId') || '';
  const defaultRegistration = searchParams.get('registration') || '';
  const defaultTenure = searchParams.get('tenure') || '';
  const defaultTags = searchParams.get('tags')?.split(',') || [];
  const defaultPhoneNumbers = (
    searchParams.get('phoneNumbers')?.split(',') || []
  ).map((phone) => formatPhoneNumber(phone));

  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState<string[]>(defaultTags);
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>(defaultPhoneNumbers);
  const [currentPhone, setCurrentPhone] = useState('');
  const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioType | null>(null);

  const {
    getCustomers,
    setShowFilters,
    customerTags,
    portfolios,
    getPortfolios,
    portfolioDetails,
  } = useCustomersContext();

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();    

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    setLoading(true);

    try {
      const params = {
        portfolioId: selectedPortfolio?.id,
        name: data.name as string,
        email: data.email as string,
        taxId: onlyDigits(data.taxId as string),
        registration: data.registration as string,
        tenure: data.tenure as string,
        tags: tags.length > 0 ? tags.join(',') : undefined,
        phoneNumbers: phoneNumbers.length > 0
          ? phoneNumbers.map((phone) => onlyDigits(phone)).join(',')
          : undefined,
      };

      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== undefined && v !== '')
      );

      const newParams = new URLSearchParams(filteredParams as { [key: string]: string });

      setSearchParams(newParams);

      await getCustomers({
        ...filteredParams,
      });
      setShowFilters(false);
    } catch {
      toast.error('Ocorreu em erro ao aplicar os filtros. Tente novamente!');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTag = (currentTag: string) => {
    setTags((prev) => prev.filter((tag) => tag !== currentTag));
  };

  const tagsList = customerTags.filter((tag) => !tags.includes(tag));

  const handleAddPhone = () => {
    if (phoneNumbers.includes(currentPhone)) {
      toast.info('Número de telefone já adicionado.');
      return;
    }

    if (currentPhone && PHONE_NUMBER.test(currentPhone)) {
      setPhoneNumbers((prev) => [...prev, currentPhone]);
      setCurrentPhone('');
      return;
    }

    toast.error('Número de telefone inválido.');
  };

  const handleRemovePhoneNumber = (currentPhoneNumber: string) => {
    setPhoneNumbers((prev) => prev.filter((phoneNumber) => phoneNumber !== currentPhoneNumber));
  };

  useEffect(() => {
    if (portfolioDetails && searchParams.get('portfolioId') === portfolioDetails.id) {
      setSelectedPortfolio(portfolioDetails);
    }
  }, [portfolioDetails, searchParams])

  return (
    <Card.Root
      size="sm"
      variant="subtle"
      bg="grayscale.fg"
      my={4}
      p={4}
      display="flex"
      justifyContent="center"
      cursor="pointer"
    >
      <Text fontWeight={500} mb={3}>
        Filtros
      </Text>
      <form onSubmit={onSubmit}>
        <Grid templateColumns="repeat(6, 1fr)" gap="2">
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <Field.Root>
              <Field.Label>Nome</Field.Label>
              <Input
                name="name"
                placeholder="Pesquisar por nome"
                variant="subtle"
                defaultValue={defaultName}
                onKeyDown={handleKeyDown}
              />
            </Field.Root>
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <Field.Root>
              <Field.Label>CPF</Field.Label>
                <CpfInput
                  name="taxId"
                  placeholder="Pesquisar pelo CPF..."
                  variant="subtle"
                  defaultValue={defaultTaxId}
                  onKeyDown={handleKeyDown}
                />
            </Field.Root>
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <Field.Root>
              <Field.Label>E-mail</Field.Label>
              <Input
                name="email"
                placeholder="Pesquisar pelo e-mail"
                variant="subtle"
                defaultValue={defaultEmail}
                onKeyDown={handleKeyDown}
              />
            </Field.Root>
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <Field.Root>
              <Field.Label>Matrícula</Field.Label>
              <Input
                name="registration"
                placeholder="Pesquisar pela matrícula"
                variant="subtle"
                defaultValue={defaultRegistration}
                onKeyDown={handleKeyDown}
              />
            </Field.Root>
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <Field.Root>
              <Field.Label>Vínculo</Field.Label>
              <Input
                name="tenure"
                placeholder="Pesquisar pelo vínculo"
                variant="subtle"
                defaultValue={defaultTenure}
                onKeyDown={handleKeyDown}
              />
            </Field.Root>
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <PortfolioSelect
              portfolios={portfolios}
              onSearch={async (text) => await getPortfolios({ query: text })}
              value={selectedPortfolio}
              onChange={(portfolio) => setSelectedPortfolio(portfolio)}
            />
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <Text fontSize="sm" mb={1} fontWeight={500}>
              Telefone
            </Text>
            <PhoneInput
              value={currentPhone}
              onChange={(e) => setCurrentPhone(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddPhone();
                }
              }}
              placeholder="(00) 00000-0000"
              variant="subtle"
            />
            <Text fontSize="xs" color="fg.muted">Pressione Enter para adicionar o telefone</Text>
            {phoneNumbers.length > 0 && (
              <Flex gap={1} flexWrap="wrap" mt={2}>
                {phoneNumbers.map((phoneNumber) => (
                  <Tag.Root key={phoneNumber} size="lg" bg="argos.fg" color="grayscale" fontWeight={600}>
                    <Tag.Label>{phoneNumber}</Tag.Label>
                    <Tag.EndElement>
                      <Tag.CloseTrigger
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleRemovePhoneNumber(phoneNumber)}
                      />
                    </Tag.EndElement>
                  </Tag.Root>
                ))}
              </Flex>
            )}
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <Text fontSize="sm" mb={1} fontWeight={500}>
              Tags
            </Text>
            {tagsList.length > 0 && (
              <NativeSelect.Root size="md" variant="subtle">
                <NativeSelect.Field
                  value=""
                  placeholder="Filtrar por tag"
                  onChange={(e) => setTags((prev) => [...prev, e.target.value])}
                >
                  {tagsList.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            )}
            {tags.length > 0 && (
              <Flex gap={1} flexWrap="wrap" mt={2}>
                {tags.map((tag) => (
                  <Tag.Root key={tag} size="lg" bg="argos.fg" color="grayscale" fontWeight={600}>
                    <Tag.Label>{tag}</Tag.Label>
                    <Tag.EndElement>
                      <Tag.CloseTrigger
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleRemoveTag(tag)}
                      />
                    </Tag.EndElement>
                  </Tag.Root>
                ))}
              </Flex>
            )}
          </GridItem>   
        </Grid>
        <Flex justifyContent="flex-end" mt={4} gap={1}>
          <Button size="sm" variant="ghost" loading={loading} onClick={() => setShowFilters(false)}>
            Fechar
          </Button>
          <Button size="sm" type="submit" loading={loading}>
            Aplicar
          </Button>
        </Flex>
      </form>
    </Card.Root>
  );
};

export default Filters;
